import React from 'react';
import { GrServices } from "react-icons/gr";
import { FaNetworkWired, FaCheck, FaMapMarkerAlt, FaShieldAlt, FaTruck, FaDatabase } from 'react-icons/fa';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import { useMyContext } from '../../../context/MyContext'
import '../../../css/custom-scrollbar.css'

const LogisticsServicePage = () => {
  const { isDarkMode, isToggleSidebar } = useMyContext();

  const services = [
    {
      icon: FaNetworkWired,
      title: 'API Integration',
      description: 'Seamless connectivity with your existing systems through robust and flexible API solutions.',
      accent: 'from-blue-500 to-cyan-400',
      glow: 'group-hover:shadow-blue-500/30',
      tag: 'Connectivity'
    },
    {
      icon: FaCheck,
      title: 'Order Confirmation',
      description: 'Real-time order tracking and instant confirmation to provide transparency and peace of mind.',
      accent: 'from-emerald-500 to-green-400',
      glow: 'group-hover:shadow-emerald-500/30',
      tag: 'Real-time'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Fraud Detection',
      description: 'Advanced algorithmic protection to identify and prevent potential fraudulent activities.',
      accent: 'from-fuchsia-500 to-purple-400',
      glow: 'group-hover:shadow-fuchsia-500/30',
      tag: 'Protection'
    },
    {
      icon: FaShieldAlt,
      title: 'Security Protocols',
      description: 'Comprehensive security measures to protect your data and ensure compliance.',
      accent: 'from-orange-500 to-amber-400',
      glow: 'group-hover:shadow-orange-500/30',
      tag: 'Compliance'
    },
    {
      icon: FaTruck,
      title: 'Logistics Tracking',
      description: 'End-to-end tracking of shipments with real-time updates and precise location monitoring.',
      accent: 'from-indigo-500 to-blue-400',
      glow: 'group-hover:shadow-indigo-500/30',
      tag: 'Visibility'
    },
    {
      icon: FaDatabase,
      title: 'Warehousing and fulfillment',
      description: "Efficient storage, inventory management, and order fulfillment solutions to streamline logistics and enhance supply chain efficiency.",
      accent: 'from-teal-500 to-cyan-400',
      glow: 'group-hover:shadow-teal-500/30',
      tag: 'Fulfillment'
    }
  ];

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'} transition-colors duration-300`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div 
            className="fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20 animate-slide-in"
            style={window.innerWidth < 768 ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' } : {}}
          >
            <Sidebar />
          </div>
        )}
        <div className="relative overflow-y-auto  pt-2 pb-4 custom-scrollbar bg-gray-100 ml-8 px-4 sm:px-6 lg:px-8 dark:bg-darkmodeColor dark:text-white">

          {/* Ambient background mesh */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-indigo-400/10 dark:bg-indigo-500/10 rounded-full blur-3xl"></div>
            <div className="absolute top-40 right-0 w-96 h-96 bg-cyan-400/10 dark:bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]" style={{
              backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
              backgroundSize: '24px 24px'
            }}></div>
          </div>

          <div className="relative max-w-7xl mx-auto">

            {/* Hero */}
            <div className="text-center  pt-4">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 bg-gradient-to-tr from-indigo-500 to-cyan-400 rounded-full blur-2xl opacity-30 animate-pulse"></div>
                  <div className="relative bg-white dark:bg-darkmodeColor p-4 rounded-full shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
                    <GrServices className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
                  </div>
                </div>
              </div>

              <span className="inline-block mb-3 text-xs font-semibold tracking-[0.2em] uppercase text-indigo-500 dark:text-cyan-400">
                Pickupxpress
              </span>

              <h1 className="text-xl sm:text-xl md:text-xl font-black tracking-tight bg-gradient-to-r from-indigo-600 via-blue-500 to-cyan-500 dark:from-indigo-400 dark:via-blue-400 dark:to-cyan-300 bg-clip-text text-transparent leading-tight">
                Logistics Services,
               Built for Motion
              </h1>
              <p className=" text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Comprehensive, connected solutions that keep every shipment visible, secure, and on time — from checkout to doorstep.
              </p>

              {/* Signature: animated route line */}
              <div className="relative  max-w-md mx-auto h-8">
                <svg viewBox="0 0 400 32" className="w-full h-full">
                  <line x1="10" y1="16" x2="390" y2="16" stroke="currentColor" strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" className="text-gray-300 dark:text-gray-700" />
                  <circle cx="10" cy="16" r="5" className="fill-indigo-500" />
                  <circle cx="390" cy="16" r="5" className="fill-cyan-400" />
                  <circle cy="16" r="4" className="fill-cyan-500">
                    <animate attributeName="cx" values="10;390;10" dur="4s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>
            </div>

            {/* Bento grid of services */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={index}
                    className={`group relative rounded-2xl p-[1px] bg-gradient-to-br ${service.accent} opacity-90 hover:opacity-100 transition-all duration-300 animate-fade-in-up`}
                    style={{ animationDelay: `${index * 90}ms` }}
                  >
                    <div className={`relative h-full rounded-2xl bg-white/90 dark:bg-darkmodeColor backdrop-blur-xl p-6 transition-all duration-300 group-hover:-translate-y-1 shadow-lg ${service.glow} group-hover:shadow-2xl`}>
                      <div className="flex items-start justify-between mb-5">
                        <div className={`relative inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${service.accent} shadow-lg`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-[11px] font-semibold tracking-wide uppercase text-gray-400 dark:text-gray-500 mt-2">
                          {service.tag}
                        </span>
                      </div>

                      <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                        {service.title}
                      </h2>
                      <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                        {service.description}
                      </p>

                      <div className={`absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r ${service.accent} scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogisticsServicePage;